import { useTypedSelector } from "@lib/redux";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { GetRowKey } from "antd/es/table/interface";
import { TableProps } from "antd/lib";
import styled, { css } from "styled-components";
import useScreen from "@lib/hooks/useScreen";
import AppMobileTable, { AppMobileTableExtraProps } from "./AppMobileTable";
import { blue } from "@ant-design/colors";
import { TableColumnConfig } from "@lib/types/General";
import { useMemo } from "react";
import clsx from "clsx";
import { TableHeaderFilter } from "@lib/types";

export type AppTableProps<T> = TableProps<T> & {
  dataSource: T[];
  columns: ColumnsType<T>;
  loading: boolean;
  rowKey: string | keyof T | GetRowKey<T>;
  striped?: boolean;
  mobileResponsive?: boolean;
  mobileTableProps?: AppMobileTableExtraProps;
  minusHeight?: number;
  highlightedRecord?: Record<string, any>;
  highlightedRecordColor?: string;
  columnsConfig?: TableColumnConfig<T>;
  header?: {
    title?: React.ReactNode;
  };
  filters?: TableHeaderFilter[];
  handleRemoveFilters?: (keys: string[]) => void;
  showSummary?: boolean;
};

// ${({ striped }) =>
//   striped &&
//   css`
//     .ant-table-row:nth-child(even) {
//       background-color: ${({ theme }) =>
//         theme.palette.background.default} !important;
//     }
//   `}
const StyledTable = styled(Table as any)<AppTableProps<any>>`
  ${({ highlightedRecord, highlightedRecordColor }) =>
    highlightedRecord &&
    css`
      .ant-table-row[data-row-key="${highlightedRecord.id}"] {
        background-color: ${() => highlightedRecordColor || blue[3]} !important;
        color: ${({ theme }) => theme.palette.text.primary} !important;

        a {
          color: ${({ theme }) => theme.palette.text.primary} !important;
        }
      }
    `}

  border-radius: 0px !important;

  .ant-table-header,
  .ant-table-container,
  .ant-table-container table > thead > tr:first-child > *:last-child {
    border-radius: 0px !important;
  }
`;

const AppTable = <T extends Record<string, any>>(props: AppTableProps<T>) => {
  const {
    mobileResponsive = true,
    minusHeight = 0,
    mobileTableProps,
    striped = true,
    showSummary = true,
    summary,
  } = props;
  const { header, footer } = useTypedSelector((state) => state.screen);
  const { isMobile } = useScreen();
  const totalMinusHeight = header.height + (footer.enabled ? footer.height : 0);
  const scrollHeight = `calc(100vh - ${totalMinusHeight + 190 + minusHeight}px)`;

  const scroll = useMemo(() => {
    return {
      x: props?.scroll?.x || "max-content",
      y: props?.scroll?.y || scrollHeight,
    };
  }, [scrollHeight, props?.scroll]);

  if (mobileResponsive && isMobile) {
    return (
      <AppMobileTable
        dataSource={props.dataSource}
        columns={props.columns as ColumnsType<Record<string, any>>}
        loading={props.loading}
        rowKey={props.rowKey as string | GetRowKey<Record<string, any>>}
        striped={striped}
        onRow={props.onRow}
        onChange={props.onChange as TableProps<Record<string, any>>["onChange"]}
        filters={props.filters}
        handleRemoveFilters={props.handleRemoveFilters}
        {...mobileTableProps}
        hiddenColumns={
          [
            ...(props?.mobileTableProps?.hiddenColumns || []),
            ...(props?.columnsConfig?.hiddenColumns || []),
          ] as string[]
        }
      />
    );
  }

  return (
    <StyledTable
      pagination={false}
      size={props?.size || "small"}
      {...props}
      summary={showSummary ? summary : undefined}
      scroll={scroll}
      striped={striped}
      className={clsx({ "ant-striped-table": striped })}
    />
  );
};

export default AppTable;
